import React, {useCallback, useEffect, useRef, useState} from 'react';
import CatalogTaskCreateModal from './CatalogTaskCreateModal';
import {orderAdminApi} from './orderAdminApi';
import styles from './OrderAdminDashboard.module.css';

const STATUS_LABELS = {
    draft: 'Draft',
    submitted: 'Submitted',
    pending_approval: 'Awaiting approval',
    production: 'Production',
    delivering: 'Delivering',
    ready_for_invoice: 'Ready for invoice',
    complete: 'Complete',
    cancelled: 'Cancelled',
    issued: 'Invoice issued',
    paid: 'Paid',
};

const ORDER_STATUSES = ['draft', 'submitted', 'pending_approval', 'production', 'delivering', 'ready_for_invoice', 'complete', 'cancelled'];
const ORDER_PAGE_SIZE = 20;
const EPISODE_EDITABLE_STATUSES = new Set(['production', 'delivering']);
const MAX_EPISODES_PER_REQUEST = 500;

const ORDER_WORKFLOW = [
    {status: 'draft', description: 'Create the order and finish the scope, hours, and due date.'},
    {status: 'submitted', description: 'The order is ready for Admin pricing review.'},
    {status: 'pending_approval', description: 'If Admin changes the rate, an organization owner or an Admin can approve the new price.'},
    {status: 'production', description: 'Pricing is approved. Order details are locked and production work is underway.'},
    {status: 'delivering', description: 'Jobs contribute delivered hours to each Order task. The owner can approve once every task reaches its requested hours.'},
    {status: 'ready_for_invoice', description: 'The organization owner accepted delivery. Admin can now attach an Invoice PDF.'},
    {status: 'complete', description: 'Admin received payment and marked the Invoice paid, completing every Order it includes.'},
];

function money(cents) {
    if (cents == null) return 'Pending';
    return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(Number(cents) / 100);
}

function rate(value) {
    return value == null ? 'Pending' : `$${Number(value).toLocaleString(undefined, {maximumFractionDigits: 2})}/hr`;
}

function date(value) {
    if (!value) return '—';
    const parsed = new Date(String(value).length === 10 ? `${value}T00:00:00` : value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
}

function hours(value, maximumFractionDigits = 2) {
    return Number(value || 0).toLocaleString(undefined, {maximumFractionDigits});
}

function reportedEpisodeCount(order) {
    const count = Number(order?.reported_episode_count || 0);
    return Number.isFinite(count) && count > 0 ? count : 0;
}

function hasReportedBadData(order) {
    return reportedEpisodeCount(order) > 0 || Number(order?.reported_bad_seconds || 0) > 0;
}

function BadDataBadge({order}) {
    if (!hasReportedBadData(order)) return null;
    const count = reportedEpisodeCount(order);
    return (
        <span
            className={styles.badDataBadge}
            title={`${count ? `${count} reported Episode${count === 1 ? '' : 's'}` : 'Bad data reported'}${Number(order?.reported_bad_hours || 0) > 0 ? ` · ${hours(order.reported_bad_hours)} hrs excluded` : ''}`}
        >
            {count ? `Bad data reported · ${count}` : 'Bad data reported'}
        </span>
    );
}

function parseEpisodeIds(value) {
    const tokens = String(value || '').split(/[\s,;]+/).map((token) => token.trim()).filter(Boolean);
    if (!tokens.length) return [];
    const ids = tokens.map((token) => {
        const normalized = token.replace(/^EP-/i, '');
        if (!/^\d+$/.test(normalized)) throw new Error(`“${token}” is not a valid Episode ID.`);
        const episodeId = Number(normalized);
        if (!Number.isSafeInteger(episodeId) || episodeId <= 0) throw new Error(`“${token}” is not a valid Episode ID.`);
        return episodeId;
    });
    return Array.from(new Set(ids));
}

function parseJobId(value) {
    const normalized = String(value || '').trim().replace(/^JOB-/i, '');
    if (!/^\d+$/.test(normalized)) throw new Error('Enter a valid Job ID, such as JOB-55.');
    const jobId = Number(normalized);
    if (!Number.isSafeInteger(jobId) || jobId <= 0) throw new Error('Enter a valid Job ID, such as JOB-55.');
    return jobId;
}

function jobProgress(job) {
    const assigned = Number(job?.hours_assigned || 0);
    const logged = Number(job?.hours_logged || 0);
    const percent = assigned > 0 ? Math.min(100, Math.round((logged / assigned) * 100)) : 0;
    const status = logged <= 0 ? 'Queued' : assigned > 0 && logged >= assigned ? 'Complete' : 'Active';
    return {assigned, logged, percent, status};
}

function operatorLabel(job, operatorIdentitiesById = {}) {
    const directoryIdentity = operatorIdentitiesById[String(job?.assigned_user_id)] || {};
    const identity = String(
        job?.assigned_user_email
        || directoryIdentity.email
        || job?.assigned_user_solana_address
        || directoryIdentity.solana_receive_address
        || '',
    ).trim();
    return identity ? `Operator · ${identity}` : `Operator #${job?.assigned_user_id}`;
}

function Status({value}) {
    return <span className={`${styles.status} ${styles[value] || ''}`}>{STATUS_LABELS[value] || value}</span>;
}

function OrderListLoading() {
    return <div className={styles.empty} role="status">Loading…</div>;
}

function OrderDetailLoading() {
    return <div className={styles.orderDetailEmpty} role="status"><strong>Loading…</strong></div>;
}

const EMPTY_ORDER = {
    robot_type_id: null,
    robot: null,
    tasks: [{task_id: null, name: '', hours: '', is_custom: false}],
    due_date: '',
    hourly_rate: '',
    spec_link: '',
    notes: '',
};

export default function OrderAdminDashboard({adminToken}) {
    const [section, setSection] = useState('orders');
    const [organizations, setOrganizations] = useState([]);
    const [taskTypes, setTaskTypes] = useState([]);
    const [robotTypes, setRobotTypes] = useState([]);
    const [orders, setOrders] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [organizationFilter, setOrganizationFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    const [editor, setEditor] = useState(null);
    const [confirmation, setConfirmation] = useState(null);
    const [approvalRate, setApprovalRate] = useState('');
    const [invoiceSelectionRequest, setInvoiceSelectionRequest] = useState(null);
    const [detailRefreshVersion, setDetailRefreshVersion] = useState(0);

    const notify = useCallback((type, text) => setMessage({type, text}), []);

    const loadAll = useCallback(async () => {
        if (!adminToken) return;
        setLoading(true);
        try {
            const taskTypesRequest = orderAdminApi.listTaskScenarios(adminToken)
                .then((data) => ({data, error: null}))
                .catch((error) => ({data: [], error}));
            const robotTypesRequest = orderAdminApi.listRobotTypes(adminToken)
                .then((data) => ({data, error: null}))
                .catch((error) => ({data: [], error}));
            const invoicesRequest = orderAdminApi.listInvoices(adminToken, organizationFilter)
                .then((data) => ({data, error: null}))
                .catch((error) => ({data: [], error}));
            const [nextOrganizations, nextOrders] = await Promise.all([
                orderAdminApi.listOrganizations(adminToken),
                orderAdminApi.listOrders(adminToken, {
                    organizationId: organizationFilter,
                    // The status selector belongs to the Orders tab. Invoices
                    // always need the full organization Order set so their
                    // ready-for-invoice picker cannot inherit a stale filter.
                    status: section === 'orders' ? statusFilter : '',
                }),
            ]);
            setOrganizations(nextOrganizations || []);
            setOrders(nextOrders || []);
            setSelectedOrder((current) => current
                ? (nextOrders || []).find((order) => order.id === current.id) || null
                : null);
            // The Orders workspace is usable as soon as its list and organization
            // labels arrive. Slower supplemental catalogs continue in parallel.
            if (section === 'orders') setLoading(false);

            const [taskTypesResult, robotTypesResult, invoicesResult] = await Promise.all([
                taskTypesRequest,
                robotTypesRequest,
                invoicesRequest,
            ]);
            setInvoices(invoicesResult.data || []);
            setTaskTypes(taskTypesResult.data || []);
            setRobotTypes(robotTypesResult.data || []);
            if (taskTypesResult.error) {
                notify('error', `Orders loaded, but task scenarios are unavailable: ${taskTypesResult.error.message}`);
            } else if (robotTypesResult.error) {
                notify('error', `Orders loaded, but Robots are unavailable: ${robotTypesResult.error.message}`);
            } else if (invoicesResult.error) {
                notify('error', `Orders loaded, but Invoices are unavailable: ${invoicesResult.error.message}`);
            }
        } catch (error) {
            notify('error', error.message || 'Failed to load Order Management.');
        } finally {
            setLoading(false);
        }
    }, [adminToken, notify, organizationFilter, section, statusFilter]);

    useEffect(() => {
        void loadAll();
    }, [loadAll]);

    const refreshOrder = async (orderId) => {
        const order = await orderAdminApi.getOrder(adminToken, orderId);
        setSelectedOrder(order);
        setOrders((current) => current.map((item) => item.id === order.id ? order : item));
        setApprovalRate(order.hourly_rate == null ? '' : String(order.hourly_rate));
        return order;
    };

    const refreshAll = async () => {
        const selectedOrderId = selectedOrder?.id;
        await loadAll();
        if (selectedOrderId) {
            try {
                await refreshOrder(selectedOrderId);
            } catch (error) {
                notify('error', error.message || `Orders refreshed, but ${selectedOrderId} details could not be refreshed.`);
            }
        }
        setDetailRefreshVersion((current) => current + 1);
    };

    const openOrder = async (order) => {
        setSelectedOrder(order);
        setApprovalRate(order.hourly_rate == null ? '' : String(order.hourly_rate));
        try {
            await refreshOrder(order.id);
        } catch (error) {
            notify('error', error.message || 'Could not load order.');
        }
    };

    const openOrganization = async (organization) => {
        setBusy(true);
        try {
            const detail = await orderAdminApi.getOrganization(adminToken, organization.id);
            setSelectedOrganization(detail);
        } catch (error) {
            notify('error', error.message || 'Could not load organization members.');
        } finally {
            setBusy(false);
        }
    };

    const runAction = async (action, successText) => {
        setBusy(true);
        try {
            const result = await action();
            notify('success', successText);
            await loadAll();
            if (typeof result?.id === 'string' && result.id.startsWith('ORD-')) {
                await refreshOrder(result.id);
            }
            return result;
        } catch (error) {
            notify('error', error.message || 'Order action failed.');
            throw error;
        } finally {
            setBusy(false);
        }
    };

    const confirmInvoiceCompletion = (invoice) => setConfirmation({
        title: `Mark ${invoice.id} as complete?`,
        message: `Double-check before continuing. Invoice: ${invoice.id}. Orders to complete: ${(invoice.order_ids || []).join(', ') || 'None'}. This will mark the Invoice paid and every listed Order complete.`,
        label: 'Mark as complete',
        action: () => runAction(() => orderAdminApi.markInvoicePaid(adminToken, invoice.id), 'Invoice marked paid and included Orders completed.'),
    });

    if (!adminToken) return <div className={styles.empty}>Admin authentication is required.</div>;

    return (
        <section className={styles.dashboard}>
            <header className={styles.header}>
                <div>
                    <h1>Order Management</h1>
                    <p>Organizations, production orders, pricing approval, delivery status, and invoice PDFs.</p>
                </div>
                <button type="button" className={styles.refreshButton} disabled={loading} onClick={() => void refreshAll()}>{loading ? 'Refreshing…' : 'Refresh'}</button>
            </header>

            <OrderWorkflowGuide/>

            {message ? <div className={`${styles.message} ${styles[message.type]}`}><span>{message.text}</span><button type="button" onClick={() => setMessage(null)}>×</button></div> : null}

            <nav className={styles.sectionTabs}>
                <button type="button" className={section === 'orders' ? styles.activeTab : ''} onClick={() => setSection('orders')}>Orders</button>
                <button type="button" className={section === 'organizations' ? styles.activeTab : ''} onClick={() => setSection('organizations')}>Organizations</button>
                <button type="button" className={section === 'invoices' ? styles.activeTab : ''} onClick={() => { setInvoiceSelectionRequest(null); setSection('invoices'); }}>Invoices</button>
            </nav>

            {section === 'orders' ? (
                <OrdersSection
                    adminToken={adminToken}
                    taskTypes={taskTypes}
                    organizations={organizations}
                    orders={orders}
                    invoices={invoices}
                    loading={loading}
                    organizationFilter={organizationFilter}
                    statusFilter={statusFilter}
                    onOrganizationFilter={setOrganizationFilter}
                    onStatusFilter={setStatusFilter}
                    selectedOrder={selectedOrder}
                    detailRefreshVersion={detailRefreshVersion}
                    approvalRate={approvalRate}
                    onApprovalRate={setApprovalRate}
                    onOpen={openOrder}
                    onClose={() => setSelectedOrder(null)}
                    onNotify={notify}
                    onOrderUpdated={(order) => {
                        setSelectedOrder(order);
                        setOrders((current) => current.map((item) => item.id === order.id ? order : item));
                        setApprovalRate(order.hourly_rate == null ? '' : String(order.hourly_rate));
                    }}
                    onRefreshOrder={refreshOrder}
                    onTaskCatalogChanged={async () => {
                        const nextTaskTypes = await orderAdminApi.listTaskScenarios(adminToken);
                        setTaskTypes(nextTaskTypes);
                        return nextTaskTypes;
                    }}
                    onCreate={() => setEditor({mode: 'create', organizationId: organizationFilter, order: null})}
                    onEdit={(order) => setEditor({mode: 'edit', organizationId: order.organization_id, order})}
                    busy={busy}
                    onApprove={() => runAction(
                        () => orderAdminApi.approveOrder(adminToken, selectedOrder.id, approvalRate),
                        selectedOrder.status === 'pending_approval'
                            ? 'Order approved by Admin and moved to Production.'
                            : Number(approvalRate) === Number(selectedOrder.hourly_rate)
                            ? 'Rate accepted; order moved to Production.'
                            : 'New rate sent to the organization owner for approval.',
                    )}
                    onDeliver={() => runAction(() => orderAdminApi.markDelivering(adminToken, selectedOrder.id), 'Order marked Delivering.')}
                    onAddInvoice={(order) => {
                        setInvoiceSelectionRequest((current) => ({
                            orderId: order.id,
                            revision: (current?.revision || 0) + 1,
                        }));
                        setSection('invoices');
                    }}
                    onMarkComplete={confirmInvoiceCompletion}
                    onCancel={() => setConfirmation({
                        title: `Cancel ${selectedOrder.id}?`,
                        message: 'The order will be cancelled and cannot resume. Confirm only after checking the organization and order ID.',
                        label: 'Cancel order',
                        action: () => runAction(() => orderAdminApi.cancelOrder(adminToken, selectedOrder.id), 'Order cancelled.'),
                    })}
                />
            ) : section === 'organizations' ? (
                <OrganizationsSection
                    organizations={organizations}
                    busy={busy}
                    onOpen={openOrganization}
                    onCreate={(values) => runAction(() => orderAdminApi.createOrganization(adminToken, values), 'Organization created and first owner assigned.')}
                    onAddOwner={(organizationId, ownerEmail) => runAction(() => orderAdminApi.addOwner(adminToken, organizationId, ownerEmail), 'Organization owner added.')}
                    onAddInternalOwner={(organizationId, ownerEmail) => runAction(
                        () => orderAdminApi.addInternalOwner(adminToken, organizationId, ownerEmail),
                        'Internal PrismaX owner added.',
                    )}
                />
            ) : (
                <InvoicesSection
                    organizations={organizations}
                    orders={orders}
                    invoices={invoices}
                    busy={busy}
                    selectionRequest={invoiceSelectionRequest}
                    onIssue={(orderIds, file) => runAction(() => orderAdminApi.issueInvoice(adminToken, orderIds, file), 'Invoice PDF issued.')}
                    onMarkPaid={confirmInvoiceCompletion}
                />
            )}

            {editor ? (
                <OrderEditor
                    editor={editor}
                    organizations={organizations}
                    taskTypes={taskTypes}
                    robotTypes={robotTypes}
                    busy={busy}
                    onClose={() => setEditor(null)}
                    onSave={async (organizationId, values) => {
                        const result = await runAction(
                            () => editor.mode === 'edit'
                                ? orderAdminApi.updateOrder(adminToken, editor.order.id, values)
                                : orderAdminApi.createOrder(adminToken, organizationId, values),
                            editor.mode === 'edit' ? 'Order updated.' : 'Order created.',
                        );
                        setEditor(null);
                        setSelectedOrder(result);
                    }}
                />
            ) : null}

            {selectedOrganization ? (
                <OrganizationDetailModal
                    organization={selectedOrganization}
                    onClose={() => setSelectedOrganization(null)}
                    onRemoveMember={(member) => setConfirmation({
                        title: `Remove ${member.email || `user ${member.user_id}`}?`,
                        message: `This removes the ${member.is_owner ? 'owner' : 'member'} from ${selectedOrganization.name} and revokes access to its Orders and My Data. This cannot be undone.`,
                        label: 'Remove member',
                        action: async () => {
                            const result = await runAction(
                                () => orderAdminApi.removeOrganizationMember(
                                    adminToken,
                                    selectedOrganization.id,
                                    member.user_id,
                                ),
                                'Organization member removed.',
                            );
                            setSelectedOrganization(result);
                        },
                    })}
                    onRemoveInternalOwner={async (userId) => {
                        const result = await runAction(
                            () => orderAdminApi.removeInternalOwner(adminToken, selectedOrganization.id, userId),
                            'Internal PrismaX owner removed.',
                        );
                        setSelectedOrganization(result);
                    }}
                />
            ) : null}

            <ConfirmModal
                confirmation={confirmation}
                busy={busy}
                onClose={() => setConfirmation(null)}
            />
        </section>
    );
}

function OrderWorkflowGuide() {
    return (
        <details className={styles.workflowGuide}>
            <summary>
                <div className={styles.workflowSummary}>
                    <strong>Order workflow</strong>
                    <span>Draft → Submitted → Awaiting approval → Production → Delivering → Ready for invoice → Complete</span>
                </div>
                <span className={styles.workflowChevron} aria-hidden="true">›</span>
            </summary>
            <div className={styles.workflowContent}>
                <ol className={styles.workflowSteps}>
                    {ORDER_WORKFLOW.map((step, index) => (
                        <li key={step.status}>
                            <div className={styles.workflowStepHeader}>
                                <span className={styles.workflowStepNumber}>{index + 1}</span>
                                <Status value={step.status}/>
                            </div>
                            <p>{step.description}</p>
                        </li>
                    ))}
                </ol>
                <p className={styles.workflowNote}><Status value="cancelled"/> is a final stop available only before Production. Invoice PDFs are managed separately in the Invoices tab.</p>
            </div>
        </details>
    );
}

function OrdersSection(props) {
    const {
        adminToken, taskTypes, organizations, orders, invoices, loading, organizationFilter, statusFilter,
        onOrganizationFilter, onStatusFilter, selectedOrder, detailRefreshVersion, approvalRate,
        onApprovalRate, onOpen, onClose, onCreate, onEdit, onNotify, onOrderUpdated, onRefreshOrder,
        onTaskCatalogChanged, busy,
        onApprove, onDeliver, onAddInvoice, onMarkComplete, onCancel,
    } = props;
    const [page, setPage] = useState(1);
    const [episodes, setEpisodes] = useState([]);
    const [episodesLoading, setEpisodesLoading] = useState(false);
    const [episodesError, setEpisodesError] = useState('');
    const [episodeEditorOpen, setEpisodeEditorOpen] = useState(false);
    const [episodeInput, setEpisodeInput] = useState('');
    const [pendingEpisodeIds, setPendingEpisodeIds] = useState([]);
    const [episodeFormError, setEpisodeFormError] = useState('');
    const [episodeBusy, setEpisodeBusy] = useState(false);
    const episodeRequestId = useRef(0);
    const [jobs, setJobs] = useState([]);
    const [jobsLoading, setJobsLoading] = useState(false);
    const [jobsError, setJobsError] = useState('');
    const [operatorIdentitiesById, setOperatorIdentitiesById] = useState({});
    const [jobEditorMode, setJobEditorMode] = useState(null);
    const linkedInvoice = invoices.find((invoice) => (invoice.order_ids || []).includes(selectedOrder?.id));
    const hasLinkedInvoice = Boolean(selectedOrder?.invoice || linkedInvoice);
    const [jobBusy, setJobBusy] = useState(false);
    const [jobToDetach, setJobToDetach] = useState(null);
    const [taskResolution, setTaskResolution] = useState(null);
    const jobRequestId = useRef(0);
    const organizationName = (id) => organizations.find((organization) => Number(organization.id) === Number(id))?.name || `Org ${id}`;
    const totalPages = Math.max(1, Math.ceil(orders.length / ORDER_PAGE_SIZE));
    const visibleOrders = orders.slice((page - 1) * ORDER_PAGE_SIZE, page * ORDER_PAGE_SIZE);
    const canManageEpisodes = Boolean(selectedOrder && EPISODE_EDITABLE_STATUSES.has(selectedOrder.status));
    const existingEpisodeIds = new Set(episodes.map((episode) => Number(episode.episode_id)));
    const reportedEpisodes = episodes.filter((episode) => episode.reported_bad);
    const coveredTaskIds = new Set(jobs.flatMap((job) => (job.tasks || []).map((task) => Number(task.task_id))));
    const uncoveredTasks = (selectedOrder?.tasks || []).filter((task) => (
        task.task_id == null || !coveredTaskIds.has(Number(task.task_id))
    ));

    const resetEpisodeEditor = useCallback(() => {
        setEpisodeEditorOpen(false);
        setEpisodeInput('');
        setPendingEpisodeIds([]);
        setEpisodeFormError('');
    }, []);

    const loadOrderEpisodes = useCallback(async (orderId) => {
        const requestId = ++episodeRequestId.current;
        setEpisodesLoading(true);
        setEpisodesError('');
        try {
            const result = await orderAdminApi.listOrderEpisodes(adminToken, orderId);
            if (requestId !== episodeRequestId.current) return null;
            setEpisodes(Array.isArray(result?.episodes) ? result.episodes : []);
            return result;
        } catch (error) {
            if (requestId !== episodeRequestId.current) return null;
            setEpisodes([]);
            setEpisodesError(error.message || 'Could not load this Order’s Episodes.');
            return null;
        } finally {
            if (requestId === episodeRequestId.current) setEpisodesLoading(false);
        }
    }, [adminToken]);

    const loadOrderJobs = useCallback(async (orderId) => {
        const requestId = ++jobRequestId.current;
        setJobsLoading(true);
        setJobsError('');
        try {
            const result = await orderAdminApi.listOrderJobs(adminToken, orderId);
            if (requestId !== jobRequestId.current) return null;
            setJobs(Array.isArray(result) ? result : []);
            return result;
        } catch (error) {
            if (requestId !== jobRequestId.current) return null;
            setJobs([]);
            setJobsError(error.message || 'Could not load this Order’s Jobs.');
            return null;
        } finally {
            if (requestId === jobRequestId.current) setJobsLoading(false);
        }
    }, [adminToken]);

    useEffect(() => {
        setPage(1);
    }, [organizationFilter, statusFilter]);

    useEffect(() => {
        let cancelled = false;
        orderAdminApi.listApprovedOperators(adminToken)
            .then((operators) => {
                if (cancelled) return;
                setOperatorIdentitiesById(Object.fromEntries(
                    (operators || [])
                        .filter((operator) => operator?.user_id != null && (
                            String(operator?.email || '').trim()
                            || String(operator?.solana_receive_address || '').trim()
                        ))
                        .map((operator) => [String(operator.user_id), {
                            email: String(operator.email || '').trim(),
                            solana_receive_address: String(operator.solana_receive_address || '').trim(),
                        }]),
                ));
            })
            .catch(() => {
                if (!cancelled) setOperatorIdentitiesById({});
            });
        return () => { cancelled = true; };
    }, [adminToken, detailRefreshVersion]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    useEffect(() => {
        resetEpisodeEditor();
        setJobEditorMode(null);
        setJobToDetach(null);
        setTaskResolution(null);
        setEpisodes([]);
        setJobs([]);
        setEpisodesError('');
        setJobsError('');
        if (!selectedOrder?.id) {
            episodeRequestId.current += 1;
            jobRequestId.current += 1;
            setEpisodesLoading(false);
            setJobsLoading(false);
            return;
        }
        void loadOrderEpisodes(selectedOrder.id);
        void loadOrderJobs(selectedOrder.id);
    }, [detailRefreshVersion, loadOrderEpisodes, loadOrderJobs, resetEpisodeEditor, selectedOrder?.id]);

    const queueEpisodeIds = (rawValue) => {
        try {
            const parsed = parseEpisodeIds(rawValue);
            const next = Array.from(new Set([
                ...pendingEpisodeIds,
                ...parsed.filter((episodeId) => !existingEpisodeIds.has(episodeId)),
            ]));
            if (next.length > MAX_EPISODES_PER_REQUEST) {
                throw new Error(`Add no more than ${MAX_EPISODES_PER_REQUEST} Episodes at once.`);
            }
            setPendingEpisodeIds(next);
            setEpisodeInput('');
            setEpisodeFormError(parsed.length && next.length === pendingEpisodeIds.length
                ? 'Those Episodes are already attached or already queued.'
                : '');
            return next;
        } catch (error) {
            setEpisodeFormError(error.message || 'Enter valid Episode IDs.');
            return null;
        }
    };

    const handleAddEpisodes = async () => {
        let episodeIds = pendingEpisodeIds;
        if (episodeInput.trim()) {
            const parsed = queueEpisodeIds(episodeInput);
            if (!parsed) return;
            episodeIds = parsed;
        }
        if (!episodeIds.length) {
            setEpisodeFormError('Enter at least one Episode ID.');
            return;
        }

        setEpisodeBusy(true);
        setEpisodeFormError('');
        try {
            const result = await orderAdminApi.addOrderEpisodes(adminToken, selectedOrder.id, episodeIds);
            if (result?.order?.id) onOrderUpdated(result.order);
            await loadOrderEpisodes(selectedOrder.id);
            const addedCount = result?.added_episode_ids?.length || 0;
            onNotify(
                'success',
                addedCount
                    ? `${addedCount} Episode${addedCount === 1 ? '' : 's'} added to ${selectedOrder.id}.`
                    : `All entered Episodes were already attached to ${selectedOrder.id}.`,
            );
            resetEpisodeEditor();
        } catch (error) {
            const text = error.message || 'Could not add Episodes to this Order.';
            setEpisodeFormError(text);
            onNotify('error', text);
        } finally {
            setEpisodeBusy(false);
        }
    };

    const handleRemoveEpisode = async (episodeId) => {
        setEpisodeBusy(true);
        try {
            const result = await orderAdminApi.removeOrderEpisode(adminToken, selectedOrder.id, episodeId);
            if (result?.order?.id) onOrderUpdated(result.order);
            await loadOrderEpisodes(selectedOrder.id);
            onNotify('success', `EP-${episodeId} removed from ${selectedOrder.id}.`);
        } catch (error) {
            onNotify('error', error.message || 'Could not remove the Episode from this Order.');
        } finally {
            setEpisodeBusy(false);
        }
    };

    const refreshJobsAndOrder = async (successMessage) => {
        await Promise.all([
            loadOrderJobs(selectedOrder.id),
            loadOrderEpisodes(selectedOrder.id),
            onRefreshOrder(selectedOrder.id),
        ]);
        setJobEditorMode(null);
        onNotify('success', successMessage);
    };

    const handleJobSaved = async (job, action) => {
        setJobBusy(true);
        try {
            await refreshJobsAndOrder(
                action === 'created'
                    ? `JOB-${job.job_id} created and linked to ${selectedOrder.id}.`
                    : `JOB-${job.job_id} linked to ${selectedOrder.id}.`,
            );
        } catch (error) {
            onNotify('error', error.message || 'The Job changed, but the Order could not be refreshed.');
        } finally {
            setJobBusy(false);
        }
    };

    const handleDetachJob = async (job) => {
        setJobBusy(true);
        try {
            await orderAdminApi.setJobOrder(adminToken, job.job_id, null, true);
            await refreshJobsAndOrder(`JOB-${job.job_id} unlinked from ${selectedOrder.id}.`);
            setJobToDetach(null);
        } catch (error) {
            onNotify('error', error.message || 'Could not unlink this Job.');
            throw error;
        } finally {
            setJobBusy(false);
        }
    };

    const linkCustomTask = async (orderItem, taskId, {created = false} = {}) => {
        const order = await orderAdminApi.linkOrderItemTask(
            adminToken,
            selectedOrder.id,
            orderItem.id,
            taskId,
        );
        onOrderUpdated(order);
        let catalogRefreshError = null;
        if (created) {
            try {
                await onTaskCatalogChanged();
            } catch (refreshError) {
                catalogRefreshError = refreshError;
            }
        }
        setTaskResolution(null);
        const mappedName = order.tasks.find((task) => task.id === orderItem.id)?.name || `Task #${taskId}`;
        onNotify(
            catalogRefreshError ? 'error' : 'success',
            catalogRefreshError
                ? `“${orderItem.name}” was linked to ${mappedName}, but the Task catalog could not refresh. Reload to see it in searches.`
                : `“${orderItem.name}” linked to ${mappedName}.`,
        );
        return order;
    };

    return (
        <>
            <div className={styles.toolbar}>
                <select aria-label="Organization filter" value={organizationFilter} onChange={(event) => onOrganizationFilter(event.target.value)}>
                    <option value="">All organizations</option>
                    {organizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}
                </select>
                <select aria-label="Status filter" value={statusFilter} onChange={(event) => onStatusFilter(event.target.value)}>
                    <option value="">All statuses</option>
                    {ORDER_STATUSES.map((value) => <option key={value} value={value}>{STATUS_LABELS[value]}</option>)}
                </select>
                <span>{loading ? 'Loading…' : `${orders.length} orders`}</span>
                <button type="button" className={styles.primaryButton} disabled={loading} onClick={onCreate}>Create order</button>
            </div>
            <div className={styles.ordersSplit}>
                <div className={styles.orderListPane}>
                    <div className={styles.orderListRows}>
                        {loading ? <OrderListLoading/> : visibleOrders.map((order) => (
                            <button
                                type="button"
                                className={`${styles.orderListRow} ${selectedOrder?.id === order.id ? styles.orderListRowSelected : ''}`}
                                key={order.id}
                                onClick={() => void onOpen(order)}
                            >
                                <span className={styles.orderListTop}>
                                    <span className={styles.orderListNameGroup}>
                                        <strong className={styles.orderListClient}>{organizationName(order.organization_id)}</strong>
                                        <span className={styles.orderListId}>{order.id}</span>
                                    </span>
                                    <span className={styles.orderListBadges}>
                                        {order.status !== 'cancelled' && (order.tasks || []).some((task) => task.is_custom && task.task_id == null) ? <span className={styles.mappingBadge}>Needs task mapping</span> : null}
                                        <BadDataBadge order={order}/>
                                        <Status value={order.status}/>
                                    </span>
                                </span>
                                <span className={styles.orderListDescription}>{order.tasks?.[0]?.name || '—'}{order.tasks?.length > 1 ? ` +${order.tasks.length - 1}` : ''}</span>
                                <span className={styles.orderListMeta}>
                                    <strong className={styles.orderListHours}>{hours(order.total_hours)} hrs</strong> · {money(order.total_value_usd_cents)} · due {date(order.due_date)}
                                </span>
                            </button>
                        ))}
                        {!orders.length && !loading ? <div className={styles.empty}>No orders match these filters.</div> : null}
                    </div>
                    <div className={styles.orderPager}>
                        <button type="button" disabled={loading || page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>← Prev</button>
                        <span>Page {page} of {totalPages}</span>
                        <button type="button" disabled={loading || page >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>Next →</button>
                    </div>
                </div>

                {loading ? <OrderDetailLoading/> : selectedOrder ? (
                    <div className={styles.orderDetailPane}>
                        <div className={styles.detailHeader}>
                            <div><h2>{selectedOrder.id} <Status value={selectedOrder.status}/></h2><p>{organizationName(selectedOrder.organization_id)} · created {date(selectedOrder.created_at)}</p></div>
                            <button type="button" aria-label="Close order detail" className={styles.closeButton} onClick={onClose}>×</button>
                        </div>
                        <div className={styles.detailGrid}>
                            <div className={styles.card}>
                                <h3>Order summary</h3>
                                <SummaryRow label="Robot" value={selectedOrder.robot?.product_name || '—'}/>
                                <SummaryRow label="Total hours" value={hours(selectedOrder.total_hours)}/>
                                <SummaryRow label="Hourly rate" value={rate(selectedOrder.hourly_rate)}/>
                                <SummaryRow label="Total value" value={money(selectedOrder.total_value_usd_cents)}/>
                                <SummaryRow label="Due date" value={date(selectedOrder.due_date)}/>
                                <SummaryRow label="Task list" value={selectedOrder.spec_link ? <a href={selectedOrder.spec_link} target="_blank" rel="noreferrer">Open ↗</a> : '—'}/>
                                {hasReportedBadData(selectedOrder) ? (
                                    <SummaryRow
                                        label="Bad data"
                                        value={<span className={styles.badDataSummary}>{reportedEpisodeCount(selectedOrder) ? `${reportedEpisodeCount(selectedOrder)} EP` : 'Reported'} · {hours(selectedOrder.reported_bad_hours)} hrs</span>}
                                    />
                                ) : null}
                            </div>
                            <div className={styles.card}>
                                <h3>Tasks</h3>
                                <div className={styles.orderTaskRows}>
                                    {(selectedOrder.tasks || []).map((task) => {
                                        const isUnresolvedCustom = selectedOrder.status !== 'cancelled' && task.is_custom && task.task_id == null;
                                        const canResolve = isUnresolvedCustom && (selectedOrder.allowed_actions || []).includes('edit');
                                        return <div className={`${styles.orderTaskRow} ${isUnresolvedCustom ? styles.orderTaskRowCustom : ''}`} key={task.id || task.name}>
                                            <div><strong>{task.name}</strong>{isUnresolvedCustom ? <span className={styles.customTaskBadge}>Custom · Needs mapping</span> : <small>{task.task_id ? `Task #${task.task_id}` : ''}</small>}</div>
                                            <span>{['production', 'delivering', 'ready_for_invoice', 'complete'].includes(selectedOrder.status)
                                                ? `${hours(task.delivered_hours)} / ${hours(task.hours)} hrs${task.delivery_ready ? ' · ready' : ''}`
                                                : `${hours(task.hours)} hrs`}</span>
                                            {canResolve ? <div className={styles.taskResolutionActions}>
                                                <button type="button" onClick={() => setTaskResolution({mode: 'link', item: task})}>Link existing task</button>
                                                <button type="button" className={styles.primaryButton} onClick={() => setTaskResolution({mode: 'create', item: task})}>Create task</button>
                                            </div> : null}
                                        </div>;
                                    })}
                                </div>
                            </div>
                        </div>
                        {selectedOrder.notes ? <div className={styles.notes}><strong>Notes</strong><p>{selectedOrder.notes}</p></div> : null}

                        <section className={`${styles.card} ${styles.jobsEpisodesCard}`} aria-labelledby="jobs-episodes-heading">
                            <h3 id="jobs-episodes-heading">Jobs and Episodes</h3>
                            <div className={styles.jobSectionHeading}>
                                <div className={styles.deliverySubheading}>Assigned jobs · {jobs.length}</div>
                                {canManageEpisodes && !jobEditorMode ? (
                                    <button type="button" className={styles.addJobButton} onClick={() => setJobEditorMode('create')}>+ Add Job</button>
                                ) : null}
                            </div>
                            {jobsLoading ? <p className={styles.deliveryEmpty}>Loading Jobs…</p> : null}
                            {!jobsLoading && jobsError ? (
                                <div className={styles.deliveryError}>
                                    <span>{jobsError}</span>
                                    <button type="button" onClick={() => void loadOrderJobs(selectedOrder.id)}>Retry</button>
                                </div>
                            ) : null}
                            {!jobsLoading && !jobsError && jobs.length ? (
                                <div className={styles.jobsGrid}>
                                    {jobs.map((job) => {
                                        const progress = jobProgress(job);
                                        return (
                                            <article className={styles.jobCard} key={job.job_id}>
                                                <header>
                                                    <strong>JOB-{job.job_id}</strong>
                                                    <span className={styles[`jobState${progress.status}`]}>{progress.status}</span>
                                                    <div className={styles.jobCardSpacer}/>
                                                    {canManageEpisodes ? (
                                                        <button type="button" className={styles.unlinkJobButton} disabled={jobBusy} onClick={() => setJobToDetach(job)}>Remove</button>
                                                    ) : null}
                                                </header>
                                                <div className={styles.jobMetric}>
                                                    <span>{operatorLabel(job, operatorIdentitiesById)}</span>
                                                    <strong>{hours(progress.logged)} / {hours(progress.assigned)} hrs · {Number(job.episode_count || 0)} EP</strong>
                                                </div>
                                                <div className={styles.jobProgressTrack}><span style={{width: `${progress.percent}%`}}/></div>
                                                <div className={styles.jobSecondaryMeta}>{rate(job.rate_usd_per_hour)} · due {date(job.due_date)}</div>
                                                <div className={styles.jobTasksLabel}>Tasks covered</div>
                                                {(job.tasks || []).map((task) => (
                                                    <div className={styles.jobTaskRow} key={task.task_id}>
                                                        <span>{task.task_name}</span>
                                                        <strong>{hours(task.assigned_hours)} hrs</strong>
                                                    </div>
                                                ))}
                                            </article>
                                        );
                                    })}
                                </div>
                            ) : null}
                            {!jobsLoading && !jobsError && !jobs.length ? <p className={styles.deliveryEmpty}>No Jobs assigned yet.</p> : null}
                            {!jobsLoading && !jobsError && jobs.length && uncoveredTasks.length ? (
                                <p className={styles.uncoveredTasks}>
                                    Not covered by any Job: {uncoveredTasks.map((task) => task.name).join(', ')}
                                </p>
                            ) : null}
                            {jobEditorMode ? (
                                <OrderJobEditor
                                    key={`${selectedOrder.id}-${jobEditorMode}`}
                                    adminToken={adminToken}
                                    mode={jobEditorMode}
                                    order={selectedOrder}
                                    onModeChange={setJobEditorMode}
                                    onCancel={() => setJobEditorMode(null)}
                                    onSaved={handleJobSaved}
                                    onNotify={onNotify}
                                />
                            ) : null}
                            {!canManageEpisodes ? (
                                <p className={styles.jobsReadOnlyNote}>Jobs can be created, linked, or unlinked only while the Order is in Production or Delivering.</p>
                            ) : null}

                            <div className={styles.deliveryDivider}/>
                            <div className={styles.deliverySubheading}>Episodes · {episodes.length}</div>
                            {episodesLoading ? <p className={styles.deliveryEmpty}>Loading Episodes…</p> : null}
                            {!episodesLoading && episodesError ? (
                                <div className={styles.deliveryError}>
                                    <span>{episodesError}</span>
                                    <button type="button" onClick={() => void loadOrderEpisodes(selectedOrder.id)}>Retry</button>
                                </div>
                            ) : null}
                            {!episodesLoading && !episodesError ? (
                                <div className={styles.episodeChips}>
                                    {!episodes.length ? <span className={styles.deliveryEmpty}>No Episodes attached</span> : null}
                                    {episodes.map((episode) => (
                                        <span
                                            className={`${styles.episodeChip} ${episode.manually_added ? styles.manualEpisodeChip : ''} ${episode.reported_bad ? styles.reportedEpisodeChip : ''}`}
                                            key={episode.episode_id}
                                            title={episode.reported_bad
                                                ? `Bad data reported${episode.reported_reason ? `: ${episode.reported_reason}` : ''}`
                                                : episode.job_id ? `Delivered by JOB-${episode.job_id}` : 'Added by an Admin'}
                                        >
                                            EP-{episode.episode_id}
                                            {episode.reported_bad ? <span className={styles.reportedEpisodeMark}>Reported</span> : null}
                                            {episode.manually_added && canManageEpisodes ? (
                                                <button
                                                    type="button"
                                                    aria-label={`Remove EP-${episode.episode_id}`}
                                                    disabled={episodeBusy}
                                                    onClick={() => void handleRemoveEpisode(Number(episode.episode_id))}
                                                >×</button>
                                            ) : null}
                                        </span>
                                    ))}
                                    {canManageEpisodes && !episodeEditorOpen ? (
                                        <button type="button" className={styles.addEpisodesButton} onClick={() => setEpisodeEditorOpen(true)}>+ Add Episodes</button>
                                    ) : null}
                                </div>
                            ) : null}
                            {!episodesLoading && !episodesError && reportedEpisodes.length ? (
                                <section className={styles.reportedDataPanel} aria-label="Bad data reported">
                                    <header>
                                        <div>
                                            <strong>Bad data reported</strong>
                                            <span>{reportedEpisodes.length} Episode{reportedEpisodes.length === 1 ? '' : 's'} excluded from accepted delivery</span>
                                        </div>
                                        <span>{hours(selectedOrder.reported_bad_hours)} hrs</span>
                                    </header>
                                    <div className={styles.reportedDataRows}>
                                        {reportedEpisodes.map((episode) => (
                                            <div className={styles.reportedDataRow} key={`reported-${episode.episode_id}`}>
                                                <strong>EP-{episode.episode_id}</strong>
                                                <span>{episode.reported_reason || 'No reason provided'}</span>
                                                <small>
                                                    {hours(episode.reported_duration_hours, 3)} hrs
                                                    {episode.reported_at ? ` · Reported ${date(episode.reported_at)}` : ''}
                                                </small>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ) : null}
                            {episodeEditorOpen ? (
                                <div className={styles.episodeEditor}>
                                    <div className={styles.episodeInputBox} onClick={(event) => event.currentTarget.querySelector('input')?.focus()}>
                                        {pendingEpisodeIds.map((episodeId) => (
                                            <span className={`${styles.episodeChip} ${styles.manualEpisodeChip}`} key={episodeId}>
                                                EP-{episodeId}
                                                <button type="button" aria-label={`Remove pending EP-${episodeId}`} onClick={(event) => {
                                                    event.stopPropagation();
                                                    setPendingEpisodeIds((current) => current.filter((value) => value !== episodeId));
                                                }}>×</button>
                                            </span>
                                        ))}
                                        <input
                                            aria-label="Episode IDs"
                                            autoComplete="off"
                                            spellCheck="false"
                                            placeholder={pendingEpisodeIds.length ? 'Add more IDs' : 'Paste or type Episode IDs'}
                                            value={episodeInput}
                                            onChange={(event) => { setEpisodeInput(event.target.value); setEpisodeFormError(''); }}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ',') {
                                                    event.preventDefault();
                                                    if (episodeInput.trim()) queueEpisodeIds(episodeInput);
                                                } else if (event.key === 'Backspace' && !episodeInput && pendingEpisodeIds.length) {
                                                    setPendingEpisodeIds((current) => current.slice(0, -1));
                                                }
                                            }}
                                            onPaste={(event) => {
                                                event.preventDefault();
                                                queueEpisodeIds(event.clipboardData.getData('text'));
                                            }}
                                        />
                                    </div>
                                    <button type="button" className={styles.episodeCommitButton} disabled={episodeBusy || (!pendingEpisodeIds.length && !episodeInput.trim())} onClick={() => void handleAddEpisodes()}>
                                        {episodeBusy ? 'Adding…' : pendingEpisodeIds.length > 1 ? `Add ${pendingEpisodeIds.length}` : 'Add'}
                                    </button>
                                    <button type="button" disabled={episodeBusy} onClick={resetEpisodeEditor}>Cancel</button>
                                    {episodeFormError ? <p className={styles.episodeFormError}>{episodeFormError}</p> : null}
                                    <small>Up to {MAX_EPISODES_PER_REQUEST} delivery-ready Episodes. IDs must match one of this Order’s task scenarios.</small>
                                </div>
                            ) : null}
                            {!canManageEpisodes ? <p className={styles.jobsReadOnlyNote}>Episodes can be added or removed only while the Order is in Production or Delivering.</p> : null}
                        </section>

                        <div className={styles.detailActions}>
                            {(selectedOrder.allowed_actions || []).includes('edit') ? <button type="button" onClick={() => onEdit(selectedOrder)}>Edit</button> : null}
                            {(selectedOrder.allowed_actions || []).includes('approve') ? (
                                <div className={styles.approveGroup}>
                                    <label>Confirmed hourly rate <input type="number" min="0.01" step="0.01" value={approvalRate} onChange={(event) => onApprovalRate(event.target.value)}/></label>
                                    <button type="button" className={styles.primaryButton} disabled={busy || !(Number(approvalRate) > 0)} onClick={() => void onApprove()}>Approve order</button>
                                </div>
                            ) : null}
                            {(selectedOrder.allowed_actions || []).includes('mark_delivering') ? <button type="button" className={styles.primaryButton} disabled={busy} onClick={() => void onDeliver()}>Mark delivering</button> : null}
                            {selectedOrder.status === 'ready_for_invoice' && !hasLinkedInvoice ? <button type="button" className={styles.primaryButton} disabled={busy} onClick={() => onAddInvoice(selectedOrder)}>Add invoice</button> : null}
                            {selectedOrder.status === 'ready_for_invoice' && linkedInvoice?.status === 'issued' ? <button type="button" className={styles.primaryButton} disabled={busy} onClick={() => onMarkComplete(linkedInvoice)}>Mark as complete</button> : null}
                            {(selectedOrder.allowed_actions || []).includes('cancel') ? <button type="button" className={styles.dangerButton} disabled={busy} onClick={onCancel}>Cancel order</button> : null}
                        </div>
                        <div className={styles.history}>
                            <h3>History</h3>
                            {(selectedOrder.history || []).map((event) => <div key={event.id}><strong>{String(event.type).replaceAll('_', ' ')}</strong><span>{date(event.created_at)} · {event.actor_type}</span></div>)}
                        </div>
                    </div>
                ) : (
                    <div className={styles.orderDetailEmpty}>
                        <strong>Select an Order</strong>
                        <span>Order details, Jobs, and Episodes will appear here.</span>
                    </div>
                )}
            </div>
            {jobToDetach && selectedOrder ? (
                <ConfirmModal
                    confirmation={{
                        title: `Remove JOB-${jobToDetach.job_id} from ${selectedOrder.id}?`,
                        message: Number(jobToDetach.episode_count || 0) > 0
                            ? `This only unlinks the Job; it does not delete the Job or its uploads. ${hours(jobToDetach.hours_logged)} delivered hours across ${jobToDetach.episode_count} Episodes will stop counting toward this Order.`
                            : 'This only unlinks the Job from the Order. The Job remains assigned to its Operator.',
                        label: 'Unlink job',
                        action: () => handleDetachJob(jobToDetach),
                    }}
                    busy={jobBusy}
                    onClose={() => setJobToDetach(null)}
                />
            ) : null}
            {taskResolution?.mode === 'link' && selectedOrder ? <TaskLinkModal
                task={taskResolution.item}
                taskTypes={taskTypes}
                excludedTaskIds={new Set((selectedOrder.tasks || []).filter((task) => task.task_id != null).map((task) => Number(task.task_id)))}
                onClose={() => setTaskResolution(null)}
                onLink={(taskId) => linkCustomTask(taskResolution.item, taskId)}
            /> : null}
            {taskResolution?.mode === 'create' && selectedOrder ? <CatalogTaskCreateModal
                adminToken={adminToken}
                initialScenario={taskResolution.item.name}
                onClose={() => setTaskResolution(null)}
                onCreated={(task) => linkCustomTask(taskResolution.item, task.task_id, {created: true})}
            /> : null}
        </>
    );
}

function TaskLinkModal({task, taskTypes, excludedTaskIds, onClose, onLink}) {
    const [query, setQuery] = useState(task.name || '');
    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const normalizedQuery = query.trim().toLowerCase();
    const matches = taskTypes.filter((taskType) => (
        !excludedTaskIds.has(Number(taskType.task_id))
        && (!normalizedQuery || `${taskType.task_id} ${taskType.scenario} ${taskType.environment || ''}`.toLowerCase().includes(normalizedQuery))
    )).slice(0, 20);

    return <div className={styles.modalBackdrop} role="presentation">
        <form className={styles.taskLinkModal} role="dialog" aria-modal="true" aria-labelledby="link-task-title" onSubmit={async (event) => {
            event.preventDefault();
            if (!selectedTaskId) return setError('Select a catalog Task to link.');
            setSaving(true);
            setError('');
            try {
                await onLink(selectedTaskId);
            } catch (linkError) {
                setError(linkError.message || 'Could not link this Task.');
                setSaving(false);
            }
        }}>
            <header><div><h2 id="link-task-title">Link existing Task</h2><p>Replace “{task.name}” with a canonical catalog Task.</p></div><button type="button" aria-label="Close link Task modal" disabled={saving} onClick={onClose}>×</button></header>
            <div className={styles.taskLinkBody}>
                <label>Search catalog<input autoFocus value={query} onChange={(event) => { setQuery(event.target.value); setSelectedTaskId(null); setError(''); }} placeholder="Search task name, environment, or ID"/></label>
                <div className={styles.taskLinkResults} role="listbox">
                    {matches.map((taskType) => <button
                        type="button"
                        role="option"
                        aria-selected={selectedTaskId === taskType.task_id}
                        className={selectedTaskId === taskType.task_id ? styles.taskLinkResultSelected : ''}
                        key={taskType.task_id}
                        onClick={() => { setSelectedTaskId(taskType.task_id); setError(''); }}
                    ><span><strong>{taskType.scenario}</strong><small>{taskType.environment || 'No environment'}</small></span><em>#{taskType.task_id}</em></button>)}
                    {!matches.length ? <p>No available Tasks match this search.</p> : null}
                </div>
                {error ? <div className={styles.editorError}>{error}</div> : null}
            </div>
            <footer><button type="button" disabled={saving} onClick={onClose}>Cancel</button><button type="submit" className={styles.primaryButton} disabled={saving || !selectedTaskId}>{saving ? 'Linking…' : 'Link Task'}</button></footer>
        </form>
    </div>;
}

function SummaryRow({label, value}) {
    return <div className={styles.summaryRow}><span>{label}</span><strong>{value}</strong></div>;
}

function OrderJobEditor({adminToken, mode, order, onModeChange, onCancel, onSaved, onNotify}) {
    return (
        <div className={styles.jobEditor}>
            <div className={styles.jobEditorHeader}>
                <div className={styles.jobEditorTabs}>
                    <button type="button" className={mode === 'create' ? styles.jobEditorTabActive : ''} onClick={() => onModeChange('create')}>Create linked Job</button>
                    <button type="button" className={mode === 'attach' ? styles.jobEditorTabActive : ''} onClick={() => onModeChange('attach')}>Attach existing Job</button>
                </div>
                <button type="button" className={styles.jobEditorClose} aria-label="Close Add Job" onClick={onCancel}>×</button>
            </div>
            {mode === 'create' ? (
                <CreateLinkedJobForm
                    adminToken={adminToken}
                    order={order}
                    onCancel={onCancel}
                    onSaved={(job) => onSaved(job, 'created')}
                    onNotify={onNotify}
                />
            ) : (
                <AttachExistingJobForm
                    adminToken={adminToken}
                    order={order}
                    onCancel={onCancel}
                    onSaved={(job) => onSaved(job, 'attached')}
                    onNotify={onNotify}
                />
            )}
        </div>
    );
}

function CreateLinkedJobForm({adminToken, order, onCancel, onSaved, onNotify}) {
    const eligibleTasks = (order.tasks || []).filter((task) => task.task_id != null);
    const unavailableTasks = (order.tasks || []).filter((task) => task.task_id == null);
    const today = new Date().toISOString().slice(0, 10);
    const [operators, setOperators] = useState([]);
    const [operatorsLoading, setOperatorsLoading] = useState(true);
    const [operatorId, setOperatorId] = useState('');
    const [rateValue, setRateValue] = useState('18.50');
    const [dueDate, setDueDate] = useState(order.due_date >= today ? order.due_date : today);
    const [taskValues, setTaskValues] = useState(() => Object.fromEntries(eligibleTasks.map((task) => [
        task.task_id,
        {selected: false, hours: String(Number(task.remaining_hours ?? task.hours) || task.hours)},
    ])));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setOperatorsLoading(true);
        orderAdminApi.listApprovedOperators(adminToken)
            .then((result) => {
                if (cancelled) return;
                setOperators(result || []);
            })
            .catch((loadError) => {
                if (cancelled) return;
                setError(loadError.message || 'Could not load approved Operators.');
            })
            .finally(() => {
                if (!cancelled) setOperatorsLoading(false);
            });
        return () => { cancelled = true; };
    }, [adminToken]);

    const selectedTasks = eligibleTasks.flatMap((task) => {
        const value = taskValues[task.task_id];
        if (!value?.selected) return [];
        return [{task_id: Number(task.task_id), assigned_hours: Number(value.hours)}];
    });

    const submit = async (event) => {
        event.preventDefault();
        setError('');
        if (!operatorId) {
            setError('Select an Operator.');
            return;
        }
        if (!selectedTasks.length || selectedTasks.some((task) => !(task.assigned_hours > 0))) {
            setError('Select at least one task and enter hours above zero.');
            return;
        }
        if (!(Number(rateValue) >= 0)) {
            setError('Enter an Operator rate of zero or more.');
            return;
        }
        if (!dueDate || dueDate < today) {
            setError('Due date must be today or later.');
            return;
        }

        setSaving(true);
        try {
            const job = await orderAdminApi.createLinkedJob(adminToken, order.id, {
                user_id: Number(operatorId),
                tasks: selectedTasks,
                rate_usd_per_hour: Number(rateValue),
                due_date: dueDate,
            });
            await onSaved(job);
        } catch (saveError) {
            const message = saveError.message || 'Could not create the linked Job.';
            setError(message);
            onNotify('error', message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <form className={styles.jobEditorBody} onSubmit={submit}>
            <div className={styles.jobFormGrid}>
                <label>Operator
                    <select aria-label="Job Operator" required disabled={operatorsLoading || saving} value={operatorId} onChange={(event) => setOperatorId(event.target.value)}>
                        <option value="">{operatorsLoading ? 'Loading Operators…' : 'Select Operator'}</option>
                        {operators.map((operator) => (
                            <option value={operator.user_id} key={operator.user_id}>
                                {operator.email || operator.solana_receive_address || `Operator ${operator.user_id}`} · ID {operator.user_id}
                            </option>
                        ))}
                    </select>
                </label>
                <label>Operator rate (USD/hr)
                    <input aria-label="Operator rate" type="number" min="0" step="0.01" value={rateValue} onChange={(event) => setRateValue(event.target.value)}/>
                </label>
                <label>Due date
                    <input aria-label="Job due date" type="date" min={today} value={dueDate} onChange={(event) => setDueDate(event.target.value)}/>
                </label>
            </div>
            <div className={styles.jobTaskPickerLabel}>Order tasks to assign</div>
            <div className={styles.jobTaskPicker}>
                {eligibleTasks.map((task) => {
                    const value = taskValues[task.task_id] || {selected: false, hours: ''};
                    return (
                        <div className={styles.jobTaskChoice} key={task.task_id}>
                            <label>
                                <input
                                    aria-label={`Assign ${task.name}`}
                                    type="checkbox"
                                    checked={value.selected}
                                    onChange={(event) => setTaskValues((current) => ({
                                        ...current,
                                        [task.task_id]: {...value, selected: event.target.checked},
                                    }))}
                                />
                                <span>{task.name}</span>
                            </label>
                            <div>
                                <input
                                    aria-label={`${task.name} assigned hours`}
                                    type="number"
                                    min="0"
                                    step="any"
                                    disabled={!value.selected}
                                    value={value.hours}
                                    onChange={(event) => setTaskValues((current) => ({
                                        ...current,
                                        [task.task_id]: {...value, hours: event.target.value},
                                    }))}
                                />
                                <span>hrs</span>
                            </div>
                        </div>
                    );
                })}
                {!eligibleTasks.length ? <p>No catalog-linked tasks are available for Job creation.</p> : null}
            </div>
            {unavailableTasks.length ? (
                <p className={styles.jobFormHint}>Custom tasks cannot be assigned to Jobs: {unavailableTasks.map((task) => task.name).join(', ')}.</p>
            ) : null}
            <p className={styles.jobFormHint}>The Operator rate is their production pay rate; it is separate from the customer’s Order rate.</p>
            {error ? <p className={styles.jobFormError}>{error}</p> : null}
            <div className={styles.jobFormActions}>
                <button type="button" disabled={saving} onClick={onCancel}>Cancel</button>
                <button type="submit" className={styles.primaryButton} disabled={saving || operatorsLoading || !eligibleTasks.length}>{saving ? 'Creating…' : 'Create and link Job'}</button>
            </div>
        </form>
    );
}

function AttachExistingJobForm({adminToken, order, onCancel, onSaved, onNotify}) {
    const [jobIdInput, setJobIdInput] = useState('');
    const [candidate, setCandidate] = useState(null);
    const [confirmedEpisodes, setConfirmedEpisodes] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const orderNumericId = Number(String(order.id).replace(/^ORD-/i, ''));
    const orderTaskIds = new Set((order.tasks || []).filter((task) => task.task_id != null).map((task) => Number(task.task_id)));
    const incompatibleTasks = (candidate?.tasks || []).filter((task) => !orderTaskIds.has(Number(task.task_id)));
    const alreadyLinkedHere = Number(candidate?.order_id) === orderNumericId;
    const linkedElsewhere = candidate?.order_id != null && !alreadyLinkedHere;
    const hasDeliveredEpisodes = Number(candidate?.episode_count || 0) > 0;

    const lookup = async (event) => {
        event.preventDefault();
        setError('');
        setCandidate(null);
        setConfirmedEpisodes(false);
        let jobId;
        try {
            jobId = parseJobId(jobIdInput);
        } catch (parseError) {
            setError(parseError.message);
            return;
        }
        setLoading(true);
        try {
            setCandidate(await orderAdminApi.getJob(adminToken, jobId));
        } catch (lookupError) {
            setError(lookupError.message || 'Could not find that Job.');
        } finally {
            setLoading(false);
        }
    };

    const attach = async () => {
        if (!candidate) return;
        setSaving(true);
        setError('');
        try {
            const job = await orderAdminApi.setJobOrder(
                adminToken,
                candidate.job_id,
                order.id,
                hasDeliveredEpisodes && confirmedEpisodes,
            );
            await onSaved(job);
        } catch (saveError) {
            const message = saveError.message || 'Could not attach this Job.';
            setError(message);
            onNotify('error', message);
        } finally {
            setSaving(false);
        }
    };

    const cannotAttach = !candidate || alreadyLinkedHere || linkedElsewhere || incompatibleTasks.length > 0
        || (hasDeliveredEpisodes && !confirmedEpisodes);
    return (
        <div className={styles.jobEditorBody}>
            <form className={styles.jobLookup} onSubmit={lookup}>
                <label>Job ID<input aria-label="Existing Job ID" value={jobIdInput} onChange={(event) => setJobIdInput(event.target.value)} placeholder="JOB-55"/></label>
                <button type="submit" disabled={loading || !jobIdInput.trim()}>{loading ? 'Looking up…' : 'Look up Job'}</button>
            </form>
            {candidate ? (
                <div className={styles.jobCandidate}>
                    <div><strong>JOB-{candidate.job_id}</strong><span>{operatorLabel(candidate)}</span></div>
                    <div><span>Tasks</span><strong>{(candidate.tasks || []).map((task) => task.task_name).join(', ') || '—'}</strong></div>
                    <div><span>Progress</span><strong>{hours(candidate.hours_logged)} / {hours(candidate.hours_assigned)} hrs</strong></div>
                    <div><span>Current Order</span><strong>{candidate.order_id ? `ORD-${candidate.order_id}` : 'Unlinked'}</strong></div>
                    {alreadyLinkedHere ? <p>This Job is already linked to {order.id}.</p> : null}
                    {linkedElsewhere ? <p>Remove this Job from ORD-{candidate.order_id} before attaching it here.</p> : null}
                    {incompatibleTasks.length ? <p>These Job tasks are not part of this Order: {incompatibleTasks.map((task) => task.task_name).join(', ')}.</p> : null}
                    {hasDeliveredEpisodes && !alreadyLinkedHere && !linkedElsewhere && !incompatibleTasks.length ? (
                        <label className={styles.jobConfirmEpisodes}>
                            <input type="checkbox" checked={confirmedEpisodes} onChange={(event) => setConfirmedEpisodes(event.target.checked)}/>
                            <span>I understand that this Job’s {candidate.episode_count} Episodes and {hours(candidate.hours_logged)} delivered hours will immediately count toward {order.id}.</span>
                        </label>
                    ) : null}
                </div>
            ) : null}
            {error ? <p className={styles.jobFormError}>{error}</p> : null}
            <div className={styles.jobFormActions}>
                <button type="button" disabled={saving} onClick={onCancel}>Cancel</button>
                <button type="button" className={styles.primaryButton} disabled={saving || cannotAttach} onClick={() => void attach()}>{saving ? 'Attaching…' : 'Attach Job'}</button>
            </div>
        </div>
    );
}

function OrganizationsSection({organizations, busy, onCreate, onAddOwner, onAddInternalOwner, onOpen}) {
    const [name, setName] = useState('');
    const [ownerEmail, setOwnerEmail] = useState('');
    const [organizationId, setOrganizationId] = useState('');
    const [additionalOwnerEmail, setAdditionalOwnerEmail] = useState('');
    const [internalOrganizationId, setInternalOrganizationId] = useState('');
    const [internalOwnerEmail, setInternalOwnerEmail] = useState('');
    return (
        <div className={styles.twoColumns}>
            <form className={styles.card} onSubmit={async (event) => {
                event.preventDefault();
                await onCreate({name: name.trim(), owner_email: ownerEmail.trim()});
                setName(''); setOwnerEmail('');
            }}>
                <h3>Create organization</h3>
                <label>Organization name<input required value={name} onChange={(event) => setName(event.target.value)} placeholder="Acme Robotics"/></label>
                <label>First owner email<input required type="email" value={ownerEmail} onChange={(event) => setOwnerEmail(event.target.value)} placeholder="owner@acme.com"/></label>
                <small>If the email does not have a PrismaX account, one will be created automatically. A user can belong to only one organization.</small>
                <button type="submit" className={styles.primaryButton} disabled={busy}>Create organization</button>
            </form>
            <form className={styles.card} onSubmit={async (event) => {
                event.preventDefault();
                await onAddOwner(organizationId, additionalOwnerEmail.trim());
                setAdditionalOwnerEmail('');
            }}>
                <h3>Add organization owner</h3>
                <label>Organization<select required value={organizationId} onChange={(event) => setOrganizationId(event.target.value)}><option value="">Select organization</option>{organizations.map((organization) => <option value={organization.id} key={organization.id}>{organization.name}</option>)}</select></label>
                <label>Owner email<input required type="email" value={additionalOwnerEmail} onChange={(event) => setAdditionalOwnerEmail(event.target.value)} placeholder="owner@acme.com"/></label>
                <small>Promotes an existing member or adds an unassigned PrismaX user as an owner.</small>
                <button type="submit" className={styles.primaryButton} disabled={busy}>Add owner</button>
            </form>
            <form className={styles.card} onSubmit={async (event) => {
                event.preventDefault();
                await onAddInternalOwner(internalOrganizationId, internalOwnerEmail.trim());
                setInternalOwnerEmail('');
            }}>
                <h3>Add internal PrismaX owner</h3>
                <label>Organization<select required value={internalOrganizationId} onChange={(event) => setInternalOrganizationId(event.target.value)}><option value="">Select organization</option>{organizations.map((organization) => <option value={organization.id} key={organization.id}>{organization.name}</option>)}</select></label>
                <label>PrismaX email<input required type="email" pattern=".+@prismax[.]ai" value={internalOwnerEmail} onChange={(event) => setInternalOwnerEmail(event.target.value)} placeholder="name@prismax.ai"/></label>
                <small>Hidden from Client Manage access. This account can own multiple Organizations and access their Orders and My Data.</small>
                <button type="submit" className={styles.primaryButton} disabled={busy}>Add internal owner</button>
            </form>
            <div className={`${styles.card} ${styles.fullWidth}`}>
                <h3>Organizations</h3>
                <div className={styles.orgList}>
                    {organizations.map((organization) => (
                        <button
                            type="button"
                            className={styles.orgCard}
                            key={organization.id}
                            disabled={busy}
                            onClick={() => onOpen(organization)}
                        >
                            <strong>{organization.name}</strong>
                            <span>ID {organization.id} · @{organization.email_domain}</span>
                            <small>{organization.owner_count || 0} client owners · {organization.member_count || 0} client members · {organization.internal_owner_count || 0} internal</small>
                            <em>View members →</em>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function OrganizationDetailModal({organization, onClose, onRemoveMember, onRemoveInternalOwner}) {
    const members = organization.members || [];
    const internalOwners = organization.internal_owners || [];
    const ownerCount = members.filter((member) => member.is_owner).length;
    return (
        <div className={styles.modalBackdrop} role="presentation" onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose();
        }}>
            <section className={styles.organizationDetail} role="dialog" aria-modal="true" aria-labelledby="organization-detail-title">
                <header>
                    <div>
                        <h2 id="organization-detail-title">{organization.name}</h2>
                        <p>ID {organization.id} · @{organization.email_domain}</p>
                    </div>
                    <button type="button" aria-label="Close organization details" onClick={onClose}>×</button>
                </header>
                <div className={styles.organizationDetailBody}>
                    <div className={styles.memberSummary}>
                        <strong>{members.length}</strong>
                        <span>{members.length === 1 ? 'member' : 'members'}</span>
                    </div>
                    <div className={styles.memberList}>
                        <div className={styles.memberHead}>
                            <span>Email</span><span>Role</span><span>User ID</span><span>Joined</span><span>Action</span>
                        </div>
                        {members.map((member) => {
                            const isLastOwner = member.is_owner && ownerCount === 1;
                            return (
                                <div className={styles.memberRow} key={member.user_id}>
                                    <strong>{member.email || 'No email'}</strong>
                                    <span>{member.is_owner ? 'Owner' : 'Member'}</span>
                                    <span>{member.user_id}</span>
                                    <span>{date(member.joined_at)}</span>
                                    <span
                                        className={styles.removeOwnerAction}
                                        title={isLastOwner ? 'Please add another owner before removing this one.' : undefined}
                                    >
                                        <button
                                            type="button"
                                            className={`${styles.dangerButton} ${styles.removeOwnerButton}`}
                                            disabled={isLastOwner}
                                            onClick={() => onRemoveMember(member)}
                                        >
                                            Remove
                                        </button>
                                    </span>
                                </div>
                            );
                        })}
                        {members.length === 0 ? <div className={styles.empty}>No organization members.</div> : null}
                    </div>
                    <div className={styles.internalOwnerHeading}>
                        <h3>Internal PrismaX owners</h3>
                        <p>Hidden from Client Manage access.</p>
                    </div>
                    <div className={styles.memberList}>
                        <div className={styles.internalOwnerHead}>
                            <span>Email</span><span>Role</span><span>User ID</span><span>Action</span>
                        </div>
                        {internalOwners.map((member) => (
                            <div className={styles.internalOwnerRow} key={member.user_id}>
                                <strong>{member.email}</strong>
                                <span>Internal owner</span>
                                <span>{member.user_id}</span>
                                <span className={styles.removeOwnerAction}>
                                    <button
                                        type="button"
                                        className={`${styles.dangerButton} ${styles.removeOwnerButton}`}
                                        onClick={() => onRemoveInternalOwner(member.user_id)}
                                    >
                                        Remove
                                    </button>
                                </span>
                            </div>
                        ))}
                        {internalOwners.length === 0 ? <div className={styles.empty}>No internal PrismaX owners.</div> : null}
                    </div>
                </div>
            </section>
        </div>
    );
}

function InvoicesSection({organizations, orders, invoices, busy, selectionRequest, onIssue, onMarkPaid}) {
    const [selectedIds, setSelectedIds] = useState([]);
    const [file, setFile] = useState(null);
    const readyOrders = orders.filter((order) => order.status === 'ready_for_invoice' && !order.invoice);
    const selectedOrders = readyOrders.filter((order) => selectedIds.includes(order.id));
    const selectedOrganizationId = selectedOrders[0]?.organization_id;
    const eligibleOrders = readyOrders.filter((order) => !selectedOrganizationId || Number(order.organization_id) === Number(selectedOrganizationId));
    const organizationName = (id) => organizations.find((organization) => Number(organization.id) === Number(id))?.name || `Org ${id}`;
    useEffect(() => {
        if (!selectionRequest?.orderId) return;
        setSelectedIds([selectionRequest.orderId]);
        setFile(null);
    }, [selectionRequest?.orderId, selectionRequest?.revision]);
    return (
        <>
            <form className={styles.invoiceForm} onSubmit={async (event) => {
                event.preventDefault();
                await onIssue(selectedIds, file);
                setSelectedIds([]); setFile(null); event.currentTarget.reset();
            }}>
                <div><h2>Issue invoice PDF</h2><p>Select one or more Ready for invoice Orders from the same organization.</p></div>
                <div className={styles.invoiceOrderPicker}>
                    {eligibleOrders.map((order) => <label key={order.id}><input type="checkbox" checked={selectedIds.includes(order.id)} onChange={(event) => setSelectedIds((current) => event.target.checked ? [...current, order.id] : current.filter((id) => id !== order.id))}/><strong>{order.id}</strong><span>{organizationName(order.organization_id)}</span><small>{money(order.total_value_usd_cents)}</small></label>)}
                    {!readyOrders.length ? <div className={styles.empty}>No owner-approved Orders are ready to invoice.</div> : null}
                </div>
                <div className={styles.invoiceUpload}><input required type="file" accept="application/pdf,.pdf" onChange={(event) => setFile(event.target.files?.[0] || null)}/><strong>{money(selectedOrders.reduce((sum, order) => sum + Number(order.total_value_usd_cents || 0), 0))}</strong><button type="submit" className={styles.primaryButton} disabled={busy || !selectedIds.length || !file}>Attach PDF & issue</button></div>
            </form>
            <div className={styles.tableWrap}>
                <div className={`${styles.invoiceRow} ${styles.tableHead}`}><span>Invoice</span><span>Organization</span><span>Orders</span><span>Amount</span><span>Issued</span><span>Status</span><span>Action</span></div>
                {invoices.map((invoice) => <div className={styles.invoiceRow} key={invoice.id}><strong>{invoice.id}</strong><span>{organizationName(invoice.organization_id)}</span><span>{(invoice.order_ids || []).join(', ')}</span><span>{money(invoice.amount_usd_cents)}</span><span>{date(invoice.issued_at)}</span><span><Status value={invoice.status}/></span><span>{invoice.status === 'issued' ? <button type="button" disabled={busy} onClick={() => onMarkPaid(invoice)}>Mark as complete</button> : date(invoice.paid_at)}</span></div>)}
            </div>
        </>
    );
}

function AdminTaskTypeSearch({index, task, taskTypes, onChange}) {
    const [isOpen, setIsOpen] = useState(false);
    const query = task.name.trim().toLowerCase();
    const matches = taskTypes.filter((taskType) => {
        if (!query) return true;
        return `${taskType.scenario} ${taskType.environment || ''}`.toLowerCase().includes(query);
    });
    const listId = `admin-task-type-results-${index}`;

    return <div
        className={styles.taskSearch}
        onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setIsOpen(false);
        }}
    >
        <input
            role="combobox"
            aria-label={`Task type ${index + 1}`}
            aria-autocomplete="list"
            aria-expanded={isOpen}
            aria-controls={listId}
            aria-required="true"
            autoComplete="off"
            value={task.name}
            placeholder="Type to search task types…"
            onFocus={() => setIsOpen(true)}
            onChange={(event) => {
                onChange({task_id: null, name: event.target.value, is_custom: false});
                setIsOpen(true);
            }}
            onKeyDown={(event) => {
                if (event.key === 'Escape') setIsOpen(false);
            }}
        />
        {isOpen ? <div id={listId} className={styles.taskSearchMenu} role="listbox">
            {matches.map((taskType) => <button
                type="button"
                role="option"
                aria-selected={!task.is_custom && Number(task.task_id) === Number(taskType.task_id)}
                aria-label={`${taskType.scenario}${taskType.environment ? ` — ${taskType.environment}` : ''}`}
                key={taskType.task_id}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                    onChange({task_id: taskType.task_id, name: taskType.scenario, is_custom: false});
                    setIsOpen(false);
                }}
            ><span>{taskType.scenario}</span>{taskType.environment ? <small>{taskType.environment}</small> : null}</button>)}
            {!matches.length ? <span className={styles.taskSearchEmpty}>No task types found.</span> : null}
        </div> : null}
    </div>;
}

function OrderEditor({editor, organizations, taskTypes, robotTypes, busy, onClose, onSave}) {
    const original = editor.order || EMPTY_ORDER;
    const emptyTask = () => ({
        task_id: null,
        name: '',
        hours: '',
        is_custom: false,
    });
    const [organizationId, setOrganizationId] = useState(String(editor.organizationId || original.organization_id || ''));
    const [robotTypeId, setRobotTypeId] = useState(
        original.robot_type_id == null
            ? (editor.mode === 'create' ? String(robotTypes[0]?.machine_id || '') : '')
            : String(original.robot_type_id),
    );
    const [tasks, setTasks] = useState((original.tasks || EMPTY_ORDER.tasks).map((task) => ({
        ...task,
        task_id: task.task_id ?? null,
        name: task.name || '',
    })));
    const [dueDate, setDueDate] = useState(original.due_date || '');
    const [hourlyRate, setHourlyRate] = useState(original.hourly_rate == null ? '' : String(original.hourly_rate));
    const [specLink, setSpecLink] = useState(original.spec_link || '');
    const [notes, setNotes] = useState(original.notes || '');
    const [formError, setFormError] = useState('');
    const updateTask = (index, values) => setTasks((current) => current.map((task, taskIndex) => taskIndex === index ? {...task, ...values} : task));
    return (
        <div className={styles.modalBackdrop}>
            <form className={styles.editor} onSubmit={async (event) => {
                event.preventDefault();
                if (editor.mode === 'create' && !robotTypeId) {
                    setFormError('Select a Robot.');
                    return;
                }
                if (tasks.some((task) => task.name.trim() && task.task_id == null && !task.is_custom)) {
                    setFormError('Select a task type from the catalog.');
                    return;
                }
                const normalizedTasks = tasks.map((task) => ({
                    task_id: task.is_custom || task.task_id == null ? null : Number(task.task_id),
                    name: task.name.trim(),
                    hours: Number(task.hours),
                    is_custom: Boolean(task.is_custom),
                })).filter((task) => task.name && task.hours > 0);
                if (!normalizedTasks.length) {
                    setFormError('Add at least one task type with hours.');
                    return;
                }
                setFormError('');
                await onSave(organizationId, {
                    ...(robotTypeId ? {robot_type_id: Number(robotTypeId)} : {}),
                    tasks: normalizedTasks,
                    due_date: dueDate,
                    hourly_rate: hourlyRate ? Number(hourlyRate) : null,
                    spec_link: specLink.trim() || null,
                    notes: notes.trim(),
                });
            }}>
                <header><div><h2>{editor.mode === 'edit' ? `Edit ${original.id}` : 'Create production order'}</h2><p>Admin-created orders with a rate wait for organization owner or Admin approval.</p></div><button type="button" onClick={onClose}>×</button></header>
                <div className={styles.editorBody}>
                    <label><span className={styles.fieldLabel}>Organization <span className={styles.required}>*</span></span><select aria-label="Organization" required disabled={editor.mode === 'edit'} value={organizationId} onChange={(event) => setOrganizationId(event.target.value)}><option value="">Select organization</option>{organizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}</select></label>
                    <div className={styles.fieldLabel}>Robot <span className={styles.required}>*</span></div>
                    <div className={styles.robotOptions} role="group" aria-label="Robot">
                        {robotTypes.map((robot) => <button
                            type="button"
                            key={robot.machine_id}
                            className={robotTypeId === String(robot.machine_id) ? styles.robotOptionActive : ''}
                            aria-pressed={robotTypeId === String(robot.machine_id)}
                            onClick={() => setRobotTypeId(String(robot.machine_id))}
                        >{robot.product_name}</button>)}
                        {!robotTypes.length ? <span className={styles.robotEmpty}>No Robots are available.</span> : null}
                    </div>
                    <small>Demonstrations are captured on the selected robot platform.</small>
                    <div className={styles.fieldLabel}>Task types &amp; hours <span className={styles.required}>*</span></div>
                    <div className={styles.taskEditor}>
                        {tasks.map((task, index) => <div key={task.id || index}>
                            <AdminTaskTypeSearch index={index} task={task} taskTypes={taskTypes} onChange={(values) => updateTask(index, values)}/>
                            <input required type="number" min="0.01" step="0.01" value={task.hours} onChange={(event) => updateTask(index, {hours: event.target.value})} placeholder="hours"/>
                            <button type="button" disabled={tasks.length === 1} onClick={() => setTasks((current) => current.filter((_, taskIndex) => taskIndex !== index))}>×</button>
                        </div>)}
                        <button type="button" className={styles.addTask} onClick={() => setTasks((current) => [...current, emptyTask()])}>+ Add task type</button>
                    </div>
                    <div className={styles.fieldGrid}><label><span className={styles.fieldLabel}>Due date <span className={styles.required}>*</span></span><input aria-label="Due date" required type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)}/></label><label>Hourly rate<input type="number" min="0.01" step="0.01" value={hourlyRate} onChange={(event) => setHourlyRate(event.target.value)} placeholder="USD per hour"/></label></div>
                    <label>Task list & demos link<input type="url" value={specLink} onChange={(event) => setSpecLink(event.target.value)} placeholder="https://…"/></label>
                    <label>Notes<textarea value={notes} onChange={(event) => setNotes(event.target.value)}/></label>
                    {formError ? <div className={styles.editorError}>{formError}</div> : null}
                </div>
                <footer><button type="button" disabled={busy} onClick={onClose}>Cancel</button><button type="submit" className={styles.primaryButton} disabled={busy}>{busy ? 'Saving…' : 'Save order'}</button></footer>
            </form>
        </div>
    );
}

function ConfirmModal({confirmation, busy, onClose}) {
    if (!confirmation) return null;
    return <div className={styles.modalBackdrop}><div className={styles.confirm}><h2>{confirmation.title}</h2><p>{confirmation.message}</p><div><button type="button" disabled={busy} onClick={onClose}>Go back</button><button type="button" className={styles.dangerButton} disabled={busy} onClick={async () => {try { await confirmation.action(); onClose(); } catch { /* message already shown */ }}}>{busy ? 'Working…' : confirmation.label}</button></div></div></div>;
}
